import { useEffect, useMemo, useState } from 'react';
import { http, jwt } from '@silvertours/front-shared';
import { AccountContext } from './Context';
import { useAccountConfig, checkConsent, Services } from '../../Runtime';
import { generic as genericTracking } from '../../../src/analytics';

type AccountSessionResponse = {
  access_token: string | null;
  refresh_token: string | null;
};

const AccountProvider = ({ children }: React.PropsWithChildren) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [userAccountId, setUserAccountId] = useState<string | undefined>(
    undefined,
  );
  const { formatSessionUrl } = useAccountConfig();

  const getAccountSession = async () => {
    let resolvedUserId: string | undefined;

    try {
      const res = await http.axiosInstance.get<AccountSessionResponse>(
        formatSessionUrl(http.API.accountSession),
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      setAccessToken(res.data.access_token);
      setRefreshToken(res.data.refresh_token);

      if (res.data.access_token) {
        resolvedUserId = jwt.decodeToken(res.data.access_token)?.username;
        setUserAccountId(resolvedUserId);
      }

      return res.data;
    } catch {
      // Treat any failure to resolve the session as not authenticated.
      return false;
    } finally {
      genericTracking.trackAccountResolved({
        userId: checkConsent(Services.braze) ? resolvedUserId : undefined,
      });
    }
  };

  useEffect(() => {
    void getAccountSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const context = useMemo(
    () => ({
      accessToken,
      refreshToken,
      userAccountId,
      setAccessToken,
      setRefreshToken,
      setUserAccountId,
      clearAccountState: () => {
        setAccessToken(null);
        setRefreshToken(null);
        setUserAccountId(undefined);
      },
    }),
    [accessToken, refreshToken, userAccountId],
  );

  return (
    <AccountContext.Provider value={context}>
      {children}
    </AccountContext.Provider>
  );
};

export { AccountProvider };
